<template>
  <div v-if="photos.length" class="photo-slider" :class="{ 'photo-slider--compact': compact }">
    <div class="photo-slider-header">
      <button
        v-if="canScroll"
        type="button"
        class="photo-slider-nav"
        aria-label="Прокрутить влево"
        @click="scrollBy(-1)"
      >
        ‹
      </button>

      <div ref="trackRef" class="photo-slider-track">
        <button
          v-for="(photo, index) in photos"
          :key="photo.id || index"
          type="button"
          class="photo-slider-thumb"
          @click="openAt(index)"
        >
          <img :src="thumbSrc(photo)" :alt="photo.filename || 'Фото'" loading="lazy" />
        </button>
      </div>

      <button
        v-if="canScroll"
        type="button"
        class="photo-slider-nav"
        aria-label="Прокрутить вправо"
        @click="scrollBy(1)"
      >
        ›
      </button>
    </div>

    <div v-if="lightboxOpen" class="photo-slider-lightbox" @click.self="close">
      <button type="button" class="photo-slider-close" aria-label="Закрыть" @click="close">×</button>
      <button
        v-if="photos.length > 1"
        type="button"
        class="photo-slider-lightbox-nav photo-slider-lightbox-nav--prev"
        aria-label="Предыдущее"
        @click="prev"
      >
        ‹
      </button>
      <img :src="photos[currentIndex].url" :alt="photos[currentIndex].filename || 'Фото'" />
      <button
        v-if="photos.length > 1"
        type="button"
        class="photo-slider-lightbox-nav photo-slider-lightbox-nav--next"
        aria-label="Следующее"
        @click="next"
      >
        ›
      </button>
      <p v-if="photos.length > 1" class="photo-slider-counter">
        {{ currentIndex + 1 }} / {{ photos.length }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  photos: {
    type: Array,
    default: () => [],
  },
  compact: {
    type: Boolean,
    default: false,
  },
  delegateOpen: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['open'])

const trackRef = ref(null)
const canScroll = ref(false)
const lightboxOpen = ref(false)
const currentIndex = ref(0)

const thumbStep = computed(() => 104)

function updateCanScroll() {
  const track = trackRef.value
  if (!track) {
    canScroll.value = false
    return
  }
  canScroll.value = track.scrollWidth > track.clientWidth + 1
}

function scrollBy(direction) {
  trackRef.value?.scrollBy({ left: direction * thumbStep.value, behavior: 'smooth' })
}

function thumbSrc(photo) {
  return photo.thumb_url || photo.url
}

function openAt(index) {
  currentIndex.value = index
  if (props.delegateOpen) {
    emit('open', index)
    return
  }
  lightboxOpen.value = true
}

function close() {
  lightboxOpen.value = false
}

function prev() {
  currentIndex.value = (currentIndex.value - 1 + props.photos.length) % props.photos.length
}

function next() {
  currentIndex.value = (currentIndex.value + 1) % props.photos.length
}

function onKeydown(event) {
  if (!lightboxOpen.value) return

  if (event.key === 'Escape') close()
  else if (event.key === 'ArrowLeft') prev()
  else if (event.key === 'ArrowRight') next()
}

watch(
  () => props.photos,
  () => {
    nextTick(updateCanScroll)
  },
  { deep: true }
)

onMounted(() => {
  updateCanScroll()
  window.addEventListener('resize', updateCanScroll)
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateCanScroll)
  window.removeEventListener('keydown', onKeydown)
})

defineExpose({ openAt })
</script>

<style scoped>
.photo-slider {
  margin-top: 0.75rem;
}

.photo-slider--compact {
  margin-top: 0;
}

.photo-slider-header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.photo-slider-track {
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  flex: 1;
  min-width: 0;
}

.photo-slider-track::-webkit-scrollbar {
  display: none;
}

.photo-slider-thumb {
  flex: 0 0 auto;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 0.375rem;
  overflow: hidden;
}

.photo-slider-thumb img {
  display: block;
  width: 96px;
  height: 72px;
  object-fit: cover;
}

.photo-slider-nav {
  flex: 0 0 auto;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 999px;
  cursor: pointer;
  line-height: 1;
}

.photo-slider-lightbox {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-slider-lightbox img {
  max-width: min(90vw, 960px);
  max-height: 85vh;
  object-fit: contain;
}

.photo-slider-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
}

.photo-slider-lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 2rem;
  width: 3rem;
  height: 3rem;
  border-radius: 999px;
  cursor: pointer;
}

.photo-slider-lightbox-nav--prev {
  left: 1rem;
}

.photo-slider-lightbox-nav--next {
  right: 1rem;
}

.photo-slider-counter {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  margin: 0;
  color: #fff;
  font-size: 0.875rem;
}
</style>
